import { forwardRef } from "react";
import { ChevronRight } from "@semo/icons";
import type { ChatListItemProps } from "../types";
import { PartyAvatar } from "../atoms/PartyAvatar";
import { cn } from "@semo/shared-ui";
import { formatChatTime } from "@semo/shared-utils";

/**
 * ChatListItem - 채팅 목록 아이템 컴포넌트
 *
 * 채팅 목록에서 개별 채팅방을 표시합니다.
 * 읽지 않은 메시지가 있을 경우 강조 표시됩니다.
 */
export const ChatListItem = forwardRef<HTMLButtonElement, ChatListItemProps>(
  (
    {
      partyName,
      projectName,
      projectCode,
      lastMessage,
      timestamp,
      unreadCount,
      onClick,
      onPartyClick,
      imageUrl,
      className,
    },
    ref,
  ) => {
    const isUnread = unreadCount > 0;

    return (
      <button
        ref={ref}
        data-slot="chat-list-item"
        onClick={onClick}
        className={cn(
          "w-full backdrop-blur-xl border rounded-2xl p-5 transition-all duration-300 group text-left cursor-pointer relative overflow-hidden",
          isUnread
            ? "bg-primary/5 border-primary/25 hover:bg-primary/5 hover:border-primary/40"
            : "bg-white/70 border-slate-200/60 hover:bg-white/90 hover:border-slate-300",
          className,
        )}
      >
        <div className="flex items-start gap-4 pr-10">
          {/* Avatar — onPartyClick 이 있으면 기업 프로필로 가는 별도 클릭 영역 */}
          <div
            className="relative shrink-0"
            role={onPartyClick ? "button" : undefined}
            tabIndex={onPartyClick ? 0 : undefined}
            aria-label={onPartyClick ? `${partyName} 기업 프로필` : undefined}
            onClick={
              onPartyClick
                ? (e) => {
                    e.stopPropagation();
                    onPartyClick();
                  }
                : undefined
            }
            onKeyDown={
              onPartyClick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onPartyClick();
                    }
                  }
                : undefined
            }
          >
            <div
              className={cn(
                "w-14 h-14 rounded-xl border-2 overflow-hidden transition-all duration-300",
                "border-slate-200 group-hover:border-primary/60",
                onPartyClick && "cursor-pointer hover:border-primary",
              )}
            >
              <PartyAvatar
                name={partyName}
                imageUrl={imageUrl}
                className="w-full h-full rounded-none"
                textClassName="text-lg"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <h3 className="text-base font-medium truncate text-slate-900 ">
                  {partyName}
                </h3>
                <span
                  className={cn(
                    "shrink-0 px-2 py-0.5 rounded-md text-2xs font-medium backdrop-blur-sm border",
                    "bg-slate-100/80 border-slate-200 text-slate-600",
                  )}
                >
                  {projectName
                    ? `${projectName} · ${projectCode}`
                    : projectCode}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-4">
                <span className="text-xs text-slate-400 ">
                  {formatChatTime(timestamp)}
                </span>
                {unreadCount > 0 && (
                  <div
                    className={cn(
                      "min-w-[22px] h-[22px] px-1.5 rounded-full",
                      "flex items-center justify-center",
                      "text-2xs font-medium",
                      "bg-gradient-to-br from-red-500 to-red-600 text-white",
                    )}
                  >
                    {unreadCount}
                  </div>
                )}
              </div>
            </div>

            <p
              className={cn(
                "text-sm leading-relaxed truncate",
                isUnread ? "text-slate-900 " : "text-slate-500 ",
              )}
            >
              {lastMessage}
            </p>
          </div>
        </div>

        {/* Arrow indicator - Absolute positioned to not affect flex layout balance */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1">
          <div
            className={cn(
              "w-10 h-10 rounded-xl flex items-center justify-center",
              "bg-primary/10 text-primary-text",
            )}
          >
            <ChevronRight className="w-5 h-5" />
          </div>
        </div>
      </button>
    );
  },
);

ChatListItem.displayName = "ChatListItem";
